import { VBaseService } from "@libVue/base";
import { EOverlay, IOverlay, OverlaysConfig } from "../EOverlay";

export class OverlaysService extends VBaseService {
    private overlay: IOverlay = {
        handle: EOverlay.none,
        fragment: "",
        obj: undefined,
    };
    private prevFragment = "";

    public static isMousedown = false;

    public currentTab: string | null = null;

    public get obj(): any {
        if (this.hasOverlay()) {
            return this.overlay.obj;
        }
    }

    constructor() {
        super();
        this.initByLocationFragment();
    }

    public hasOverlay(): boolean {
        return (
            this.overlay.handle !== EOverlay.none &&
            this.overlay.handle in EOverlay
        );
    }

    public isActive(handle: EOverlay): boolean {
        return (
            this.overlay.handle !== EOverlay.none &&
            this.overlay.handle === handle
        );
    }

    public showOverlay(handle: EOverlay, fragment?: string, obj?: any): void {
        if (typeof handle === "string" && handle in EOverlay) {
            this.prevFragment = this.overlay.fragment;
            this.overlay.handle = handle;
            this.overlay.fragment =
                typeof fragment === "string" && fragment.length > 0
                    ? fragment
                    : this.findOverlayFragment(handle);
            if (typeof obj === "function") {
                // eslint-disable-next-line @typescript-eslint/no-this-alias
                const thisRef = this;
                const dataOrPromise = obj();
                if (typeof dataOrPromise.then === "function") {
                    // dataOrPromise is a promise
                    dataOrPromise.then((data: object) => {
                        thisRef.overlay.obj = data;
                    });
                } else {
                    // dataOrPromise is already the data
                    thisRef.overlay.obj = dataOrPromise;
                }
            } else {
                this.overlay.obj = obj;
            }

            this.updateDocBody();
            this.updateFragment();
        } else {
            console.warn("Overlay not found", handle);
            this.hideOverlays();
        }
    }

    public hideOverlays(onlyLoading?: boolean): void {
        if (!OverlaysService.isMousedown) {
            if (!onlyLoading || this.overlay.handle === "loading") {
                this.prevFragment = this.overlay.fragment;
                this.overlay.handle = EOverlay.none;
                this.overlay.fragment = "";
                this.overlay.obj = undefined;

                this.updateDocBody();
                this.updateFragment();
            }
        }
    }

    public toggleOverlay(handle: EOverlay, fragment?: string, obj?: any): void {
        if (this.hasOverlay()) {
            this.hideOverlays();
        } else {
            this.showOverlay(handle, fragment, obj);
        }
    }

    private updateDocBody(): void {
        // set body overflow
        if (this.overlay.handle in EOverlay) {
            if (this.overlay.handle === EOverlay.none) {
                document.body.style.overflowY = "auto";
                document.body.classList.remove("has-overlay");
            } else {
                if (!document.body.classList.contains("has-overlay")) {
                    document.body.classList.add("has-overlay");
                }
                document.body.style.overflowY = "hidden";
            }
        } else {
            document.body.classList.remove("has-overlay");
            document.body.style.overflowY = "auto";
            console.warn(
                'invalid overlay handle "' + this.overlay.handle + '"',
            );
        }
    }

    private updateFragment(): void {
        const urlParts = window.location.href.replace(/#.*?$/i, "").split("?");
        const queryStr =
            urlParts.length > 1 ? "?" + urlParts.slice(1).join("?") : "";
        if (this.overlay.fragment.length > 0) {
            console.log("changing location hash");
            window.location.hash = this.overlay.fragment;
        } else {
            // only remove fragment if previous fragment (currently active) is not empty
            if (this.prevFragment.length > 0) {
                // remove hash from URL
                console.log("removing location hash");
                if ("pushState" in window.history) {
                    window.history.pushState(
                        "",
                        document.title,
                        window.location.pathname + queryStr,
                    );
                } else {
                    window.location.hash = ""; // this leaves the '#', used as a fallback
                }
            }
        }
    }

    private findOverlayFragment(handle: string): string {
        if (
            handle in OverlaysConfig &&
            typeof OverlaysConfig[handle as EOverlay] === "object"
        ) {
            const fragment = OverlaysConfig[handle as EOverlay]?.fragment;
            if (typeof fragment === "string" && fragment.length > 0) {
                return fragment;
            }
        }
        return "";
    }

    private findOverlayHandle(fragment: string): EOverlay {
        for (const handle in OverlaysConfig) {
            if (!OverlaysConfig.hasOwnProperty(handle)) {
                continue;
            }
            const config = OverlaysConfig[handle as EOverlay];
            if (config && config.fragment === fragment) {
                return handle as EOverlay;
            }
        }
        return EOverlay.none;
    }

    private initByLocationFragment(): void {
        let fragment = window.location.hash;
        if (fragment.charAt(0) === "#") {
            fragment = fragment.substr(1);
        }

        if (fragment.length > 0) {
            const handle = this.findOverlayHandle(fragment);
            if (handle !== EOverlay.none) {
                this.overlay.handle = handle;
                this.overlay.fragment = fragment;

                this.updateDocBody();
                this.updateFragment();
            }
        }
    }

    public mousedown(): void {
        OverlaysService.isMousedown = true;
    }

    public mouseup(): void {
        setTimeout(function () {
            OverlaysService.isMousedown = false;
        }, 50);
    }

    public setCurrentTab(tab: string) {
        console.log("Setting current tab to", tab);
        this.currentTab = tab;
    }
}
